import { DualFuelCommsCheckResult } from '../../types/comms-check';
import { SingleFuelReportingView, toSingleFuelReportingViews } from './dualFuelCommsCheck';

export type Fuel = 'Elec' | 'Gas';

export type CommsStatus = 'IN_COMMS' | 'NOT_IN_COMMS' | 'UNDETERMINED';

export type CommsAction = 'CONTACT_SST' | 'CH_REBOOT' | 'POWER_CYCLE' | 'CHECK_DETAILS_RETRY' | 'CHECK_DETAILS_CH_REBOOT';

export const STATUS_TEXT: Record<CommsStatus, (fuel: Fuel) => string> = {
  IN_COMMS: (fuel) => `${fuel} meter is in comms`,
  NOT_IN_COMMS: (fuel) => `${fuel} meter is not in comms`,
  UNDETERMINED: (fuel) => `${fuel} meter comms status cannot be determined`,
};

export const ACTION_TEXT: Record<CommsAction, string> = {
  CONTACT_SST: 'Please contact SST.',
  CH_REBOOT: 'Perform a comms hub reboot and try again.',
  POWER_CYCLE: 'Perform a power cycle and try again.',
  CHECK_DETAILS_RETRY:
    'If this is the first unsuccessful response, please check the MPxN, MSN, and CHF, and then try again. If the issue persists, contact SST.',
  CHECK_DETAILS_CH_REBOOT:
    'If this is the first unsuccessful response, please check the MPxN, MSN, and CHF. If correct, perform a comms hub reboot and try again. If the issue persists, please contact SST.',
};

export const OTHER_DEVICES_TEXT = 'Please contact SST to get other devices removed from CHF';

export interface FuelCommsOutcome {
  fuel: Fuel;
  status: CommsStatus;
  action: CommsAction | null;
}

export interface DualFuelCommsOutcome {
  elec: FuelCommsOutcome;
  gas: FuelCommsOutcome;
  hasOtherDevices: boolean;
}

type MatrixState = 'success' | 'fail' | 'timeout' | 'skipped' | 'other';

function toMatrixState(state: string | null): MatrixState {
  switch (state) {
    case 'SUCCESS':
      return 'success';
    case 'FAIL':
      return 'fail';
    case 'PROCESSING':
      return 'timeout';
    case 'NOT_STARTED':
      return 'skipped';
    default:
      return 'other';
  }
}

type OutcomeRow = [CommsStatus, CommsAction | null];

const OUTCOME_MATRIX: Record<string, OutcomeRow> = {
  'success/success/success': ['IN_COMMS', null],
  'success/success/fail': ['NOT_IN_COMMS', 'CONTACT_SST'],
  'success/success/timeout': ['UNDETERMINED', 'CONTACT_SST'],
  'success/fail/success': ['NOT_IN_COMMS', 'POWER_CYCLE'],
  'success/fail/fail': ['NOT_IN_COMMS', 'CH_REBOOT'],
  'success/fail/timeout': ['NOT_IN_COMMS', 'CH_REBOOT'],
  'success/timeout/success': ['UNDETERMINED', 'POWER_CYCLE'],
  'success/timeout/fail': ['UNDETERMINED', 'CH_REBOOT'],
  'success/timeout/timeout': ['UNDETERMINED', 'CH_REBOOT'],
  'fail/success/skipped': ['UNDETERMINED', 'CHECK_DETAILS_RETRY'],
  'timeout/success/skipped': ['UNDETERMINED', 'CHECK_DETAILS_RETRY'],
  'fail/fail/skipped': ['UNDETERMINED', 'CHECK_DETAILS_CH_REBOOT'],
  'timeout/fail/skipped': ['UNDETERMINED', 'CHECK_DETAILS_CH_REBOOT'],
};

const CATCH_ALL_ROW: OutcomeRow = ['UNDETERMINED', 'CHECK_DETAILS_RETRY'];

function hasDataForEverySuccess({ xrinv, xritm }: SingleFuelReportingView['result']): boolean {
  const xrinvOk = xrinv.state !== 'SUCCESS' || !!xrinv.address;
  const xritmOk = xritm.state !== 'SUCCESS' || (!!xritm.msn && xritm.data.length > 0);
  return xrinvOk && xritmOk;
}

export function determineFuelCommsOutcome(fuel: Fuel, view: SingleFuelReportingView): FuelCommsOutcome {
  const { xrinv, xritm, xrdlg } = view.result;
  const key = [xrinv.state, xritm.state, xrdlg.state].map(toMatrixState).join('/');
  const row = hasDataForEverySuccess(view.result) ? (OUTCOME_MATRIX[key] ?? CATCH_ALL_ROW) : CATCH_ALL_ROW;
  const [status, action] = row;

  if (status === 'IN_COMMS' && !view.isMeterActive) {
    return { fuel, status: 'UNDETERMINED', action: 'CHECK_DETAILS_RETRY' };
  }
  return { fuel, status, action };
}

export function determineDualFuelCommsOutcome(result: DualFuelCommsCheckResult): DualFuelCommsOutcome {
  const { elec, gas } = toSingleFuelReportingViews(result);
  return {
    elec: determineFuelCommsOutcome('Elec', elec),
    gas: determineFuelCommsOutcome('Gas', gas),
    hasOtherDevices: result.xrdlg.state === 'SUCCESS' && result.xrdlg.data.length > 0,
  };
}

export const OTHER_DEVICES_SUMMARY_TEXT = 'Other devices found on the comms hub';

/**
 * One status sentence per fuel (Elec first), plus an other-devices line when the device log lists
 * extra devices, joined with newlines. Intended for the message handed to SST when the engineer
 * starts a chat from the error screen, so the agent can see which meter passed and which failed
 * without asking.
 */
export function summariseDualFuelCommsOutcome(result: DualFuelCommsCheckResult): string {
  const { elec, gas, hasOtherDevices } = determineDualFuelCommsOutcome(result);
  const lines = [elec, gas].map((outcome) => STATUS_TEXT[outcome.status](outcome.fuel));
  if (hasOtherDevices) {
    lines.push(OTHER_DEVICES_SUMMARY_TEXT);
  }
  return lines.join('\n');
}
